<script setup>
import { ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import BaseBubble from './Base.vue';
import Button from 'next/button/Button.vue';
import Icon from 'next/icon/Icon.vue';
import Spinner from 'next/spinner/Spinner.vue';
import { useSnakeCase } from 'dashboard/composables/useTransformKeys';
import { useMessageContext } from '../provider.js';
import { downloadFile } from '@chatwoot/utils';
import { timeStampAppendedURL } from 'dashboard/helper/URLHelper';

import GalleryView from 'dashboard/components/widgets/conversation/components/GalleryView.vue';

const emit = defineEmits(['error']);
const { t } = useI18n();

const { filteredCurrentChatAttachments, attachments } = useMessageContext();

const attachment = computed(() => {
  return attachments.value[0];
});

const hasError = ref(false);
const showGallery = ref(false);
const isDownloading = ref(false);
const isImageLoaded = ref(false);
const loadRetries = ref(0);
const sourceUrl = ref(attachment.value?.dataUrl);

const handleLoad = () => {
  isImageLoaded.value = true;
};

// A freshly landed image (received, or just sent) can 404 for an instant
// while the file is still settling in storage; <img> never retries on its
// own. Reload with a fresh cache-buster a few times before giving up, same
// pattern already used for audio (see chips/Audio.vue#onLoadError).
const handleError = () => {
  const url = attachment.value?.dataUrl;
  if (url && loadRetries.value < 3) {
    loadRetries.value += 1;
    setTimeout(() => {
      sourceUrl.value = timeStampAppendedURL(url);
    }, 1000 * loadRetries.value);
    return;
  }
  hasError.value = true;
  emit('error');
};

// The optimistic (pending) message renders this bubble before the attachment
// has a dataUrl at all. Reset once a (new) URL arrives so the bubble recovers
// on its own instead of staying stuck until a reload.
watch(
  () => attachment.value?.dataUrl,
  newUrl => {
    hasError.value = false;
    loadRetries.value = 0;
    isImageLoaded.value = false;
    sourceUrl.value = newUrl;
  }
);

const downloadAttachment = async () => {
  const { fileType, dataUrl, extension } = attachment.value;
  try {
    isDownloading.value = true;
    await downloadFile({ url: dataUrl, type: fileType, extension });
  } catch (error) {
    useAlert(t('GALLERY_VIEW.ERROR_DOWNLOADING'));
  } finally {
    isDownloading.value = false;
  }
};
</script>

<template>
  <BaseBubble
    class="overflow-hidden p-3"
    data-bubble-name="image"
    @click="showGallery = true"
  >
    <div v-if="hasError" class="flex items-center gap-1 text-center rounded-md">
      <Icon icon="i-lucide-circle-off" class="text-n-slate-11" />
      <p class="mb-0 text-n-slate-11">
        {{ $t('COMPONENTS.MEDIA.IMAGE_UNAVAILABLE') }}
      </p>
    </div>
    <div
      v-else
      class="relative group rounded-md overflow-hidden"
      :class="{ 'min-h-32 min-w-32': !isImageLoaded }"
    >
      <div
        v-if="!isImageLoaded"
        class="absolute inset-0 flex items-center justify-center bg-n-alpha-1"
      >
        <Spinner />
      </div>
      <img
        class="skip-context-menu"
        :class="{ invisible: !isImageLoaded }"
        :src="sourceUrl"
        :width="attachment.width"
        :height="attachment.height"
        @click="onClick"
        @load="handleLoad"
        @error="handleError"
      />
      <div
        class="inset-0 p-2 pointer-events-none absolute bg-gradient-to-tl from-n-slate-12/30 dark:from-n-slate-1/50 via-transparent to-transparent hidden group-hover:flex"
      />
      <div class="absolute right-2 bottom-2 hidden group-hover:flex gap-2">
        <Button xs solid slate icon="i-lucide-expand" class="opacity-60" />
        <Button
          xs
          solid
          slate
          icon="i-lucide-download"
          class="opacity-60"
          :is-loading="isDownloading"
          :disabled="isDownloading"
          @click.stop="downloadAttachment"
        />
      </div>
    </div>
  </BaseBubble>
  <GalleryView
    v-if="showGallery"
    v-model:show="showGallery"
    :attachment="useSnakeCase(attachment)"
    :all-attachments="filteredCurrentChatAttachments"
    @error="handleError"
    @close="() => (showGallery = false)"
  />
</template>
